import { useCallback, useMemo, useState } from 'react';

import { TariffGroupAttributeTypeEnum } from '@src/core/buyerApiClient';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import {
  adderResumeSelector,
  changeSelectedAttributes,
  currentProductAdderStatus,
  languageOptionsSelector,
  loadingTariffGroupAttributesSelector,
  productHasLanguageStepSelector,
  selectedProductAttributesSelector,
  tariffsState,
} from '@src/stores/product';

import { getLanguageOptionMeta } from '../core/language/getLanguageOptionMeta';
import type { LanguageOption } from '../types/language';
import { getSelectedLanguageValue } from '../core/language/getSelectedLanguageValue';
import { trackStepConfirmed } from '@src/domains/products/features/product-adder-v2/analytics/widgetTracking';

type UseLanguageSelectorReturn = {
  visible: boolean;
  loading: boolean;
  options: LanguageOption[];
  selectedLanguage: string | null;
  selectedLabel: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  value: string | null;
  selectedOption: LanguageOption | undefined;
  onSelect: (value: string) => void;
  onConfirm: () => void;
  disableConfirm: boolean;
  isAddingToCart: boolean;
};

export function useLanguageSelector(): UseLanguageSelectorReturn {
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();

  const { productId } = useAppSelector(adderResumeSelector(widgetId));
  const loading = useAppSelector(loadingTariffGroupAttributesSelector(productId ?? ''));
  const selectedAttributes = useAppSelector(selectedProductAttributesSelector(widgetId));
  const currentStatus = useAppSelector(currentProductAdderStatus(widgetId));
  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const options = useAppSelector(languageOptionsSelector(widgetId));

  const hasLanguageAttributes = useAppSelector(productHasLanguageStepSelector(productId ?? ''));

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string | null>(null);

  const selectedLanguage = useMemo(() => getSelectedLanguageValue(selectedAttributes), [selectedAttributes]);

  const selectedLabel = useMemo(() => {
    if (!selectedLanguage) {
      return null;
    }

    const fromOptions = options.find((option) => option.value === selectedLanguage);
    if (fromOptions) {
      return fromOptions.label;
    }

    return getLanguageOptionMeta(selectedLanguage).label;
  }, [options, selectedLanguage]);

  const selectedOption = useMemo(
    () => (selectedLanguage ? options.find((option) => option.value === selectedLanguage) : undefined),
    [options, selectedLanguage],
  );

  const onOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
    },
    [selectedLanguage, options],
  );

  const onSelect = useCallback((value: string) => {
    setValue(value);
  }, []);

  const onConfirm = useCallback(() => {
    if (!value) {
      return;
    }

    dispatch(
      changeSelectedAttributes({
        id: widgetId,
        value: [{ type: TariffGroupAttributeTypeEnum.Language, value }],
      }),
    );
    trackStepConfirmed(widgetId, productId ?? '', 'language');
    setOpen(false);
  }, [dispatch, value, widgetId, productId]);

  const isAddingToCart = currentStatus === 'adding_to_cart';

  const visible = useMemo(() => {
    if (loading) return true;

    if (!selectedDate) {
      return hasLanguageAttributes;
    }

    return options.length > 0;
  }, [selectedDate, hasLanguageAttributes, loading, options]);

  return {
    visible,
    loading,
    options,
    selectedLanguage,
    selectedLabel,
    open,
    onOpenChange,
    value,
    selectedOption,
    onSelect,
    onConfirm,
    disableConfirm: !value || isAddingToCart,
    isAddingToCart,
  };
}
